import type { DevexReviewLatencyGroupBy, DevexReviewLatencySizeBucket } from '@dev-console/types';
import {
  DEVEX_REVIEW_ASSIGNMENT_MODES,
  DEVEX_REVIEW_AUTHOR_TEAM_RELATIONSHIPS,
  DEVEX_REVIEW_LATENCY_SIZE_BUCKETS,
  DEVEX_REVIEW_LATENCY_TEAMS,
  DEVEX_REVIEW_OPENED_DAY_BUCKETS,
  DEVEX_REVIEW_OPENED_HOUR_BUCKETS,
  DEVEX_REVIEW_TOUCH_SURFACES,
} from '@shared/dev-console/devex-metrics';

export const GROUP_BY_LABELS: Record<DevexReviewLatencyGroupBy, string> = {
  assignment: 'Assignment mode',
  dayOfWeek: 'Opened day',
  hourOfDay: 'Opened hour',
  relationship: 'Author/team relationship',
  size: 'PR size',
  surface: 'Touch surface',
  team: 'Author team',
};

export const SIZE_BUCKET_LABELS: Record<DevexReviewLatencySizeBucket, string> = {
  XS: 'XS (<=50 LOC)',
  S: 'S (51-200 LOC)',
  M: 'M (201-500 LOC)',
  L: 'L (501-1000 LOC)',
  XL: 'XL (>1000 LOC)',
};

// Display labels per groupBy dimension, so values from different dimensions live in separate
// namespaces and can never collide or override one another. Missing entries fall back to the raw
// value (e.g. team slugs, which are shown as-is). Size buckets reuse SIZE_BUCKET_LABELS so the two
// never drift.
export const GROUP_VALUE_LABELS: Record<DevexReviewLatencyGroupBy, Record<string, string>> = {
  assignment: {
    'individual-only': 'Individual only',
    'team-only': 'Team only',
    mixed: 'Mixed',
    none: 'No assignment',
  },
  dayOfWeek: {
    'mon-thu': 'Mon-Thu',
    friday: 'Friday',
    weekend: 'Weekend',
  },
  hourOfDay: {
    'business-hours': 'Business hours',
    'off-hours': 'Off-hours',
  },
  relationship: {
    'cross-team': 'Cross-team',
    'in-team': 'In team',
    'no-team-tag': 'No team tag',
  },
  size: SIZE_BUCKET_LABELS,
  surface: {
    client: 'Client',
    mobile: 'Mobile',
    multi: 'Multi-surface',
    other: 'Other',
    server: 'Server',
    shared: 'Shared',
  },
  team: { growth: 'growth (legacy)', other: 'Other', provider: 'provider (legacy)' },
};

// Canonical display order for each dimension's group values, driving the chip/tab order. Values not
// listed sort after known values, alphabetically. Sourced from the shared bucket constants so order
// stays in sync with the value sets the backend emits.
export const GROUP_VALUE_ORDER: Record<DevexReviewLatencyGroupBy, readonly string[]> = {
  assignment: DEVEX_REVIEW_ASSIGNMENT_MODES,
  dayOfWeek: DEVEX_REVIEW_OPENED_DAY_BUCKETS,
  hourOfDay: DEVEX_REVIEW_OPENED_HOUR_BUCKETS,
  relationship: DEVEX_REVIEW_AUTHOR_TEAM_RELATIONSHIPS,
  size: DEVEX_REVIEW_LATENCY_SIZE_BUCKETS,
  surface: DEVEX_REVIEW_TOUCH_SURFACES,
  team: DEVEX_REVIEW_LATENCY_TEAMS,
};
